import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import { getDynamicRouteLink, NavigationItem } from '@lsq-frontend/common-ui';
import {
    ClaimService,
    getNavigationItemForNode,
    getRouterLinkUrlById,
    ResponsiveService
} from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { combineLatest } from 'rxjs';
import { first, map, withLatestFrom } from 'rxjs/operators';
import {
    atlas,
    settingsAtlas,
    SETTINGS_MANAGE_USERS,
    SETTINGS_MOBILE_MENU,
    SETTINGS_NOTIFICATION_PREFERENCES,
    SETTINGS_PROFILE,
    SETTINGS_RESET_PASSWORD,
    SETTINGS_SCF_DIRECT_BANK_ACCOUNTS,
    SETTINGS_SCF_MANAGE_USERS,
    SETTINGS_SCF_PLATFORM_FAQ,
    SETTINGS_SCF_PLATFORM_MOBILE_MENU,
    SETTINGS_SCF_PLATFORM_NOTIFICATION_PREFERENCES,
    SETTINGS_SCF_PLATFORM_PAYMENT_METHODS,
    supplyChainPlatformSettingsAtlas
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { SupplyChainPlatformRelationshipService } from '../supply-chain/platform/relationship/relationship.service';
import { WhiteLabelManagerService } from '@lsq-frontend/fasttrack-shared/features/white-label-service';

@Component({
    selector: 'lsq-settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class SettingsComponent implements OnInit {
    public navigationItems: NavigationItem[];
    public isPlatformIntegrationUser$ = this._claimService.isOn(
        DashboardClaim.FinancingPlatformEnrollment
    );

    public mobileMenuRouterLink$ = this.isPlatformIntegrationUser$.pipe(
        withLatestFrom(this._supplyChainPlatformRelationshipService.platformId$),
        map(([isPlatformUser, platformId]) => {
            if (isPlatformUser && platformId) {
                return getDynamicRouteLink(
                    getRouterLinkUrlById(atlas, SETTINGS_SCF_PLATFORM_MOBILE_MENU),
                    { platformId }
                );
            } else {
                return getRouterLinkUrlById(atlas, SETTINGS_MOBILE_MENU);
            }
        })
    );

    public title = this._i18n({
        value: 'Settings',
        description: 'Settings title'
    });
    public title$ = combineLatest([
        this.isPlatformIntegrationUser$,
        this._whiteLabelManagerService.whiteLabel$
    ]).pipe(
        map(([isPlatformIntegrationUser, whiteLabel]) =>
            isPlatformIntegrationUser && whiteLabel
                ? `${whiteLabel.scfPlatformProductName} ${this.title}`
                : this.title
        )
    );

    public myProfileNavigation = this._i18n({
        value: 'My Profile',
        description: 'Navigation item'
    });
    public notificationsNavigation = this._i18n({
        value: 'Notifications',
        description: 'Navigation item'
    });
    public resetPasswordNavigation = this._i18n({
        value: 'Reset Password',
        description: 'Navigation item'
    });
    public multiFactorAuthenticationNavigation = this._i18n({
        value: 'Multi-Factor Authentication',
        description: 'Navigation item'
    });
    public manageUsersNavigation = this._i18n({
        value: 'Manage Users',
        description: 'Navigation item'
    });
    public payoutSettingsNavigation = this._i18n({
        value: 'Payment Methods',
        description: 'Navigation item'
    });

    public paymentMethodsNavigation = this._i18n({
        value: 'Payment Methods',
        description: 'Navigation item'
    });
    public faqNavigation = this._i18n({
        value: 'FAQ',
        meaning: 'Frequently asked questions',
        description: 'Navigation item'
    });

    constructor(
        public responsiveService: ResponsiveService,
        private _claimService: ClaimService,
        private _i18n: I18n,
        private _supplyChainPlatformRelationshipService: SupplyChainPlatformRelationshipService,
        private _whiteLabelManagerService: WhiteLabelManagerService
    ) {}

    public ngOnInit(): void {
        this._claimService
            .isOn(DashboardClaim.FinancingVendorPortal)
            .pipe(first())
            .subscribe((isVendorPortalUser) => {
                this.navigationItems = [
                    getNavigationItemForNode(settingsAtlas, SETTINGS_PROFILE, {
                        label: this.myProfileNavigation
                    }),
                    getNavigationItemForNode(settingsAtlas, SETTINGS_RESET_PASSWORD, {
                        label: this.resetPasswordNavigation
                    }),
                    getNavigationItemForNode(settingsAtlas, SETTINGS_NOTIFICATION_PREFERENCES, {
                        label: this.notificationsNavigation
                    }),
                    getNavigationItemForNode(settingsAtlas, SETTINGS_SCF_DIRECT_BANK_ACCOUNTS, {
                        label: this.payoutSettingsNavigation
                    }),
                    getNavigationItemForNode(
                        supplyChainPlatformSettingsAtlas,
                        SETTINGS_SCF_PLATFORM_PAYMENT_METHODS,
                        {
                            label: this.paymentMethodsNavigation
                        }
                    ),
                    getNavigationItemForNode(
                        supplyChainPlatformSettingsAtlas,
                        SETTINGS_SCF_PLATFORM_NOTIFICATION_PREFERENCES,
                        {
                            label: this.notificationsNavigation
                        }
                    ),
                    getNavigationItemForNode(
                        supplyChainPlatformSettingsAtlas,
                        SETTINGS_SCF_PLATFORM_FAQ,
                        {
                            label: this.faqNavigation
                        }
                    )
                ];
                this.navigationItems.push(
                    getNavigationItemForNode(settingsAtlas, SETTINGS_MANAGE_USERS, {
                        label: this.manageUsersNavigation
                    }),
                    getNavigationItemForNode(settingsAtlas, SETTINGS_SCF_MANAGE_USERS, {
                        label: this.manageUsersNavigation
                    })
                );
            });
    }
}
